import { useCallback } from 'react';
import { EMPTY, getThirdPartyExportMessageRow } from '../../../../logic/exportUtils';
import { assertNotNil, convertMultipleSeriesDataToExcel, Dates, type ExcelCell } from 'venn-utils';
import { formatExportableSubjectWithOptionalFee } from '../../../../../legend';
import { useGetRequestSubjectFee } from '../../../../../blocks/utils/hooks/use-advisory-fee';
import type { PercentileBlockExportFunctionProps } from '../types';
import { getCurrencySymbol } from '../../../charts/chartsUtils';

export const usePercentileChartExportDataFunction = ({
  subjectsSeries,
  subjectInfos,
  exportable,
}: PercentileBlockExportFunctionProps) => {
  const getRequestSubjectFee = useGetRequestSubjectFee();
  return useCallback(() => {
    if (!exportable) {
      return [getThirdPartyExportMessageRow()];
    }

    const headers: ExcelCell[] = [
      EMPTY,
      ...subjectsSeries.map((serie, idx) => {
        if (idx === 0 || subjectsSeries[idx - 1]?.key !== serie.key) {
          const originalSubjectInfo = subjectInfos[assertNotNil(serie.key)]!;
          const asOfDate = originalSubjectInfo.asOfDate;
          return {
            value: asOfDate
              ? `${formatExportableSubjectWithOptionalFee(originalSubjectInfo.subject, false, getRequestSubjectFee({ subject: originalSubjectInfo.subject, isBenchmark: originalSubjectInfo.subject.isBenchmark }))} (As of ${Dates.toDayMonthYear(asOfDate)}) + ${formatExportableSubjectWithOptionalFee(assertNotNil(originalSubjectInfo.privateSubject), false, getRequestSubjectFee({ subject: assertNotNil(originalSubjectInfo.privateSubject) }))}`
              : `${formatExportableSubjectWithOptionalFee(originalSubjectInfo.subject, false, getRequestSubjectFee({ subject: originalSubjectInfo.subject, isBenchmark: originalSubjectInfo.subject.isBenchmark }))} + ${formatExportableSubjectWithOptionalFee(assertNotNil(originalSubjectInfo.privateSubject), false, getRequestSubjectFee({ subject: assertNotNil(originalSubjectInfo.privateSubject) }))}`,
            bold: true,
          };
        }
        return EMPTY;
      }),
    ];
    const data = convertMultipleSeriesDataToExcel(
      subjectsSeries.map((serie) => ({
        name: serie.name,
        // skip the first row for public-private asset growth which is historical and non-exportable
        series: serie.data.slice(1),
      })),
      false,
      getCurrencySymbol('USD'),
      'QUARTERLY',
    );
    return [headers, ...data];
  }, [exportable, subjectsSeries, subjectInfos, getRequestSubjectFee]);
};
